import React from 'react';
import DateFnsUtils from '@date-io/date-fns';
import { Button, createStyles, Grid, Icon, IconButton, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Theme, Typography } from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { makeStyles } from '@material-ui/styles';
import { ptBR } from 'date-fns/locale';

import { VehicleIpmModel, vehicleIpmModelInitial } from '../../../Models/VehicleIpmModel';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { EditAction } from '../../../shared/enum/EditAction';
import { VehicleContext } from '../VehicleContext';
import { handleDisplayDate, numberFormat } from '../../../shared/operators';
import { HomeContext } from '../../Home/HomeContext';

const useStyles = makeStyles((theme: Theme) => createStyles({
    paper:{
        padding: theme.spacing(3),   
    },
    tableResume: {
        backgroundColor: theme.palette.background.default,
        marginTop: theme.spacing(1)
    },
    addButton: {
        paddingRight: theme.spacing(1.5)
    } 
}));

export function VehicleIpm() {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);
    const vehicleContext = React.useContext(VehicleContext);

    const [currentIpm, setCurrentIpm] = React.useState<VehicleIpmModel>(vehicleIpmModelInitial);
    const [startOfPeriodFocused, setStartOfPeriodFocused] = React.useState<boolean>(false);

    const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false);

    const [ipmValueNotDefinedError, setIpmValueNotDefinedError] = React.useState<boolean>(false);
    const [startOfPeriodNotDefinedError, setStartOfPeriodNotDefinedError] = React.useState<boolean>(false);

    const hasError = () => {
        const ipmValueError = !((currentIpm.ipmValue != null) && (currentIpm.ipmValue > -1));
        const startOfPeriodError = !currentIpm.startOfPeriod;

        setIpmValueNotDefinedError(ipmValueError);
        setStartOfPeriodNotDefinedError(startOfPeriodError);

        return ipmValueError || startOfPeriodError;
    };

    const handleIpmValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = numberFormat(event.target.value);
        setCurrentIpm({...currentIpm, ipmValue: value});
    };

    const handleStartOfPeriodChange = (date: MaterialUiPickersDate) => {
        let dateTime = null;

        if (date) {
            const year = date.getFullYear();
            const month = date.getMonth();
            const day = date.getDate();
            dateTime = new Date(year, month, day, 0, 0, 0);
        }

        setCurrentIpm({...currentIpm, startOfPeriod: dateTime});
    };

    const handleAddNewIpm = () => {
        if (!hasError()){
            const currentVehicleIpm: VehicleIpmModel = JSON.parse(JSON.stringify(currentIpm));
            currentVehicleIpm.vehicleId = vehicleContext.vehicle.id;
            currentVehicleIpm.editAction = EditAction.INSERT;

            const vehicleIpmList: VehicleIpmModel[] = vehicleContext.vehicle.ipmList && vehicleContext.vehicle.ipmList.length > 0 
                ? JSON.parse(JSON.stringify(vehicleContext.vehicle.ipmList))
                : [];

            if (currentVehicleIpm.startOfPeriod && vehicleIpmList.length > 0) {
                const activeIpmIndex = vehicleIpmList.findIndex(i => i.endOfPeriod == null && i.editAction != EditAction.DELETE);
                const activeIpm = vehicleIpmList[activeIpmIndex];

                if (activeIpm.startOfPeriod && (activeIpm.startOfPeriod >= currentVehicleIpm.startOfPeriod)) {
                    homeContext.showWarning('Já possui um IPM com vigência neste período, é preciso excluí-lo antes de adicionar um novo.');
                    return;
                } else {
                    const newIpmDate = new Date(currentVehicleIpm.startOfPeriod);
                    newIpmDate.setSeconds(newIpmDate.getSeconds() - 1);

                    vehicleIpmList[activeIpmIndex].endOfPeriod = newIpmDate;

                    if (activeIpm.editAction != EditAction.INSERT)
                        vehicleIpmList[activeIpmIndex].editAction = EditAction.UPDATE;
                }
            }

            vehicleIpmList.push(currentVehicleIpm);
            vehicleContext.setVehicle({...vehicleContext.vehicle, ipmList : vehicleIpmList});

            setCurrentIpm(vehicleIpmModelInitial);
        }
    };

    const handleDeleteIpm = () => {
        if (deleteIndex != null &&
            vehicleContext.vehicle.ipmList &&
            vehicleContext.vehicle.ipmList.length > 0) {

            const vehicleIpmList: VehicleIpmModel[] = JSON.parse(JSON.stringify(vehicleContext.vehicle.ipmList));

            if (deleteIndex > 0) {
                let previous = 1;
                
                while (previous != 0) {
                    if (vehicleIpmList[deleteIndex - previous].editAction == EditAction.DELETE)
                        previous += 1;
                    else {
                        if (vehicleIpmList[deleteIndex - previous].editAction != EditAction.INSERT)
                            vehicleIpmList[deleteIndex - previous].editAction = EditAction.UPDATE;

                        vehicleIpmList[deleteIndex - previous].editAction = EditAction.UPDATE;
                        vehicleIpmList[deleteIndex - previous].endOfPeriod = null;
                        previous = 0;
                    }
                }
            }

            if (vehicleIpmList[deleteIndex].editAction === EditAction.INSERT)
                vehicleIpmList.splice(deleteIndex, 1);
            else if (vehicleIpmList[deleteIndex].editAction === EditAction.NONE 
                  || vehicleIpmList[deleteIndex].editAction === EditAction.UPDATE) {
                vehicleIpmList[deleteIndex].editAction = EditAction.DELETE;
            }

            setDeleteDialogOpen(false);
            vehicleContext.setVehicle({...vehicleContext.vehicle, ipmList : vehicleIpmList});
        }   
    };

    const getTitle = () => {
        return (
            <Grid item xs={12}>
                <Typography variant="h6">
                    IPM
                </Typography>
            </Grid>
        );
    };

    const getIpmValue = () => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField 
                    id='vehicle-ipm-value'
                    fullWidth
                    label="Valor do IPM"
                    variant="outlined"
                    type="number" 
                    value={currentIpm.ipmValue ? currentIpm.ipmValue : (currentIpm.ipmValue === 0 ? 0 : '')}
                    onChange={handleIpmValueChange}
                    error={ipmValueNotDefinedError && !((currentIpm.ipmValue != null) && (currentIpm.ipmValue > -1))}
                    helperText={(ipmValueNotDefinedError && !((currentIpm.ipmValue != null) && (currentIpm.ipmValue > -1))) && 'Campo obrigatório'} />
            </Grid>
        );
    };

    const getStartOfPeriod = () => {
        return (
            <Grid item xs={12} sm={6}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <DatePicker
                        fullWidth
                        autoOk
                        allowKeyboardControl
                        id="ipm-start-of-period"
                        variant="inline"
                        value={currentIpm.startOfPeriod}
                        inputVariant="outlined"
                        label="Data de início de vigência"
                        onChange={(date: MaterialUiPickersDate) => handleStartOfPeriodChange(date)}
                        format="dd/MM/yyyy"
                        onBlur={() => setStartOfPeriodFocused(false)}
                        inputRef={input => startOfPeriodFocused && input && input.focus()}
                        invalidDateMessage='Formato de data inválido'
                        open={startOfPeriodFocused}
                        disablePast
                        onClose={() => setStartOfPeriodFocused(false)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setStartOfPeriodFocused(true)}>
                                        <Icon>date_range</Icon>
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        error={startOfPeriodNotDefinedError && !currentIpm.startOfPeriod}
                        helperText={startOfPeriodNotDefinedError && !currentIpm.startOfPeriod ? 'Campo obrigatório' : ''} />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    const getAddButton = () => {
        return (
            <Grid 
                container 
                direction="column"
                justify="flex-end"
                alignItems="flex-end"
                className={classes.addButton}>
                <Grid item>
                    <Button 
                        variant="contained" 
                        color='primary'
                        onClick={handleAddNewIpm} 
                        size='large'>
                        Adicionar
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const getResumeTable = () => {
        return ( <>
            { vehicleContext.vehicle.ipmList &&  
              vehicleContext.vehicle.ipmList.filter(i => i.editAction != EditAction.DELETE).length > 0 && 
            <Grid item xs={12}>
                <Typography variant="subtitle1">Resumo do IPM</Typography>
                <TableContainer component={Paper} elevation={0} className={classes.tableResume}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">Valor</TableCell>
                                <TableCell align="center">Data início de vigência</TableCell>
                                <TableCell align="center">Data fim de vigência</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vehicleContext.vehicle.ipmList.slice(0).reverse().map((ipm: VehicleIpmModel, index: number, array: VehicleIpmModel[]) => {
                                if (ipm.editAction != EditAction.DELETE)
                                    return (
                                        <>
                                            <TableRow key={index}>
                                                <TableCell align="center">
                                                    {ipm.ipmValue ? ipm.ipmValue : (ipm.ipmValue === 0 ? 0 : '-')}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {ipm.startOfPeriod ? handleDisplayDate(ipm.startOfPeriod, '/') : '-'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {ipm.endOfPeriod ? handleDisplayDate(ipm.endOfPeriod, '/') : '-'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {!ipm.endOfPeriod && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => { setDeleteDialogOpen(true); setDeleteIndex(array.length - 1 - index); }}>
                                                            <Icon>delete_outline</Icon>
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>       
                                        </>
                                    );
                            } )
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid> }
        </>
        );
    };

    const getDeleteDialog = () => {
        return (
            <BasicConfirmDialog
                title=''
                description="Tem certeza que deseja excluir este ipm?"
                handleFirstButton={ () => handleDeleteIpm() }
                handleSecondButton={ () => setDeleteDialogOpen(false) }
                textFirstButton="Excluir"
                textSecondButton="Cancelar"
                open={deleteDialogOpen} />
        );
    };

    return (
        <>
            <Paper className={classes.paper}>
                <Grid container spacing={3}>
                    { getTitle() }
                    { getIpmValue() }
                    { getStartOfPeriod() }
                    { getAddButton() }
                </Grid> 
                { getResumeTable() }
            </Paper>
            { getDeleteDialog() }
        </>
    );
}